/**
 * Is this connection still waiting to be opened — and what is it waiting for?
 *
 * Non-`'none'` only for connections a resume deliberately left closed
 * (`services/lazyConnect.ts`) and that nothing has opened yet:
 *
 * - `'background'` — the post-restore sweep is about to dial it (or is dialing
 *   it right now). A short-lived state on a healthy resume.
 * - `'password'` — the sweep skipped it on purpose: opening it raises the
 *   single-instance password prompt, which cannot be shown for several
 *   connections at once. It opens the first time a tab needs it.
 *
 * It goes `'none'` the moment lazy connect dials the connection, when the user
 * disconnects it, and on session teardown — so a surface can render its caption
 * without ever having to guess when to stop.
 *
 * Subscribes to lazy connect's own pending set rather than mirroring it into
 * store state: that set is the authority, and a copy would have to be kept in
 * step with four separate lifecycle paths. The password half additionally reads
 * the saved connection config, which is not subscribed — editing a connection
 * to add a stored password while its row is on screen leaves the caption stale
 * until the pending set next changes, which is the moment the row disappears
 * anyway.
 */
import { useCallback, useSyncExternalStore } from 'react';
import {
  isLazyConnectPending,
  isPasswordGatedLazyConnect,
  subscribeLazyConnectPending,
} from '../services/lazyConnect';

export type LazyConnectPendingKind = 'none' | 'background' | 'password';

export function useLazyConnectPendingKind(
  connectionId: string | undefined
): LazyConnectPendingKind {
  const getSnapshot = useCallback((): LazyConnectPendingKind => {
    if (!connectionId || !isLazyConnectPending(connectionId)) return 'none';
    return isPasswordGatedLazyConnect(connectionId) ? 'password' : 'background';
  }, [connectionId]);

  return useSyncExternalStore(subscribeLazyConnectPending, getSnapshot, getSnapshot);
}
